/**
 * InlineSectionRenderer — Notion-style inline rendering for sections.
 *
 * Sections appear AS BLOCKS in the doc flow at the position the user
 * (or agent) inserted them, not as separate page-cards. Each section
 * is a collapsible card with a header (type icon + name + status) and
 * a body containing the section's paragraphs + reply-thread.
 *
 * This replaces MultiPageView (which arranged sections side-by-side
 * as separate pages) — Alex 2026-05-30: "ich stelle mir das eher
 * so inline-mäßig vor, in-place mit drop-down zum auf/zuklappen".
 *
 * Phase A (this file): read-only rendering. Phase B will add the
 * inline `+`-handle + floating menu to the editor for creating new
 * sections at the cursor position.
 */

import { useEffect, useRef, useState } from 'react'
import { CONTENT_API } from '../config'
import { getAccessToken } from '../auth'
import {
  HelpCircle,
  CheckCircle,
  Box,
  StickyNote,
  ChevronDown,
  ChevronRight,
  Vote as VoteIcon,
  Trophy,
  Scale,
  Lightbulb,
  GitPullRequest,
} from 'lucide-react'
import { InlineAdder, type SectionRef } from './InlineAdder'
import { colorFromId } from './colorFromId'

interface StructuredParagraph {
  index: number
  text: string
  author_id?: string | null
  author_name?: string | null
  author_color?: string | null
  block_id?: string | null
  commit_id?: string | null
  is_heading?: boolean
  heading_level?: number | null
  section_id?: string | null
  section_type?: 'product' | 'question' | 'task' | 'annotation' | 'vote' | null
  section_role?: 'start' | 'content' | 'reply' | 'end' | null
  section_attrs?: Record<string, unknown> | null
}

interface Props {
  paragraphs: StructuredParagraph[]
  renderMarkdown: (md: string) => string
  notifyAuthors?: string[]
  /** Click handler for the section's "reply" affordance (question + annotation only). */
  onReply?: (sectionId: string) => void
  /** Post id — required to enable the inline `+` adders between blocks. */
  postId?: number
  /** Called after the user inserts a new section. */
  onSectionCreated?: (sectionId: string) => void
  /** When true, plain paragraphs get author-color left-stripes grouped
   *  by commit_id (matches the old AN-mode behavior). Sections still
   *  render as cards. */
  showAuthors?: boolean
  /** P1.4: display name of the viewing user — used to detect whether
   *  they are Primary or CC on a section's `targets` list. Optional;
   *  without it, Lead/CC visuals stay neutral. */
  currentUserName?: string
}

const TAG_RE = /^\s*<\/?(section|reply|main)\b[^>]*>\s*$/i

/* ── Multi-Block-SVG (Wissensosmose-Whiteboards, Post #4722) ─────────
 * Kollaborative SVGs liegen bewusst als EIN Element pro Paragraph vor
 * (block-level Claims + word-level Authorship). Der Markdown-Renderer
 * sieht dadurch nur Fragmente: der <svg>-Opener wird zur leeren Box,
 * <text>-Elemente werden zu tag-gestripptem Fließtext (Alex' Fund,
 * Screenshot 119660). Diese Helper setzen die Paragraph-Sequenz vom
 * <svg>-Opener bis </svg> wieder zu EINEM inline-SVG zusammen. */

type BodySegment =
  | { kind: 'md'; paragraphs: StructuredParagraph[] }
  | { kind: 'svg'; svg: string }

function splitSvgSegments(paragraphs: StructuredParagraph[]): BodySegment[] {
  const segments: BodySegment[] = []
  let cur: BodySegment | null = null
  let svgLines: string[] | null = null
  for (const p of paragraphs) {
    const t = (p.text || '').trim()
    if (svgLines) {
      svgLines.push(p.text || '')
      if (/^<\/svg>\s*$/i.test(t)) {
        segments.push({ kind: 'svg', svg: svgLines.join('\n') })
        svgLines = null
        cur = null
      }
      continue
    }
    if (/^<svg[\s>]/i.test(t)) {
      svgLines = [p.text || '']
      cur = null
      continue
    }
    if (cur?.kind !== 'md') {
      cur = { kind: 'md', paragraphs: [] }
      segments.push(cur)
    }
    cur.paragraphs.push(p)
  }
  // Unclosed <svg> (live mid-edit): fall back to markdown so nothing
  // is swallowed while an agent is still drawing.
  if (svgLines) {
    if (cur?.kind !== 'md') {
      cur = { kind: 'md', paragraphs: [] }
      segments.push(cur)
    }
    for (const line of svgLines) {
      cur.paragraphs.push({ text: line } as StructuredParagraph)
    }
  }
  return segments
}

/* Minimaler SVG-Sanitizer: kein Script, keine Event-Handler, keine
 * javascript:-URLs, kein foreignObject. Die Inhalte stammen aus dem
 * eigenen Collab-System, aber dangerouslySetInnerHTML bleibt
 * dangerously — Defense-in-Depth kostet hier drei Regexe. */
function sanitizeSvg(svg: string): string {
  return svg
    .replace(/<script[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<foreignObject[\s\S]*?<\/foreignObject\s*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|xlink:href)\s*=\s*(["']?)\s*javascript:[^"'\s>]*\2/gi, '')
}

/* width:100% aufs Root-Element, damit das viewBox-SVG die Section
 * füllt statt auf Browser-Default (300×150) zu fallen. */
function responsiveSvg(svg: string): string {
  return svg.replace(
    /^(\s*<svg\b)/i,
    '$1 style="width:100%;height:auto;display:block"',
  )
}

interface SectionRun {
  kind: 'section'
  section_id: string
  section_type: 'product' | 'question' | 'task' | 'annotation' | 'vote'
  attrs: Record<string, unknown>
  paragraphs: StructuredParagraph[]
}

interface PlainRun {
  kind: 'plain'
  paragraphs: StructuredParagraph[]
}

export function InlineSectionRenderer({
  paragraphs,
  renderMarkdown,
  onReply,
  postId,
  notifyAuthors,
  onSectionCreated,
  showAuthors,
  currentUserName,
}: Props) {
  const runs: Array<PlainRun | SectionRun> = []
  // Pre-build the section-ref list so the Vote-Erweitert form's
  // action_target picker can choose a sibling task/question without
  // a server roundtrip (Alex Option B — Vote Governance UI).
  const existingSections: SectionRef[] = []
  let currentSection: SectionRun | null = null
  let currentPlain: PlainRun | null = null
  for (const p of paragraphs) {
    if (p.section_id && p.section_type) {
      if (!currentSection || currentSection.section_id !== p.section_id) {
        currentSection = {
          kind: 'section',
          section_id: p.section_id,
          section_type: p.section_type,
          attrs: (p.section_attrs as Record<string, unknown>) || {},
          paragraphs: [],
        }
        runs.push(currentSection)
        currentPlain = null
      }
      if (p.section_role === 'start' && p.section_attrs) {
        currentSection.attrs = p.section_attrs
      }
      currentSection.paragraphs.push(p)
    } else {
      if (!currentPlain) {
        currentPlain = { kind: 'plain', paragraphs: [] }
        runs.push(currentPlain)
        currentSection = null
      }
      currentPlain.paragraphs.push(p)
    }
  }

  // Compute the paragraph-index AFTER each run so an InlineAdder
  // between run-i and run-(i+1) can pass that index to the server
  // and the new section lands exactly where the user clicked "+".
  // First adder = before everything → index -1 (= prepend).
  const lastIdxAfterRun: number[] = []
  let cursor = -1
  for (const run of runs) {
    cursor += run.paragraphs.length
    lastIdxAfterRun.push(cursor)
    // Populate existingSections from each section run — Vote-Erweitert
    // form needs the list of task/question siblings as
    // action_target candidates. Label fallback: first non-empty
    // body paragraph, or the section_type if no body text.
    if (run.kind === 'section') {
      const attrs = run.attrs ?? {}
      const labelFromAttrs =
        (typeof attrs.question === 'string' && attrs.question.trim()) ||
        (typeof attrs.name === 'string' && attrs.name.trim()) ||
        (typeof attrs.assignee === 'string' && attrs.assignee.trim()) ||
        ''
      const labelFromBody = run.paragraphs
        .filter((p) => p.section_role === 'content')
        .map((p) => (p.text || '').trim())
        .find((t) => t.length > 0) || ''
      const label =
        labelFromAttrs ||
        labelFromBody ||
        `(${run.section_type} ohne Titel)`
      existingSections.push({
        id: run.section_id,
        type: run.section_type,
        label,
      })
    }
  }

  const adderEnabled = typeof postId === 'number'

  return (
    <>
      {adderEnabled && (
        <InlineAdder
          postId={postId!}
          paragraphIndex={-1}
          notifyAuthors={notifyAuthors}
          existingSections={existingSections}
          onCreated={onSectionCreated}
        />
      )}
      {runs.map((run, i) => {
        // Skip "ghost" sections: only the opener paragraph remains
        // after a partial delete (TipTap removed end-tag + content
        // but left the role=start). Without a role='end' marker we
        // treat it as orphaned and don't render a card. The opener
        // paragraph stays in the CRDT — needs a separate cleanup
        // pass to fully remove. Iteration F backlog item.
        if (run.kind === 'section') {
          const hasEnd = run.paragraphs.some(
            (p) => p.section_role === 'end',
          )
          if (!hasEnd) return null
        }
        const block =
          run.kind === 'plain' ? (
            showAuthors ? (
              <PlainRunWithAuthors
                key={`plain-${i}`}
                paragraphs={run.paragraphs}
                renderMarkdown={renderMarkdown}
              />
            ) : (
              <div
                key={`plain-${i}`}
                className="inline-plain"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(
                    run.paragraphs.map((p) => p.text).join('\n\n'),
                  ),
                }}
              />
            )
          ) : (
            <InlineSection
              key={run.section_id}
              run={run}
              renderMarkdown={renderMarkdown}
              postId={postId}
              onReply={onReply}
              currentUserName={currentUserName}
            />
          )
        return (
          <div key={`wrap-${i}`}>
            {block}
            {adderEnabled && (
              <InlineAdder
                postId={postId!}
                paragraphIndex={lastIdxAfterRun[i]}
                notifyAuthors={notifyAuthors}
                existingSections={existingSections}
                onCreated={onSectionCreated}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

function InlineSection({
  run,
  renderMarkdown,
  postId,
  onReply,
  currentUserName,
}: {
  run: SectionRun
  renderMarkdown: (md: string) => string
  postId?: number
  onReply?: (sectionId: string) => void
  currentUserName?: string
}) {
  const visual = visualForType(run.section_type)
  const [replyOpen, setReplyOpen] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replySaving, setReplySaving] = useState(false)
  const initialOpen = (() => {
    try {
      return localStorage.getItem(`section-open-${run.section_id}`) !== '0'
    } catch {
      return true
    }
  })()
  const [open, setOpen] = useState(initialOpen)

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev
      try {
        localStorage.setItem(`section-open-${run.section_id}`, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }

  const title = String(
    run.attrs.name || run.attrs.initiator || visual.label,
  )
  const status = (run.attrs.status as string | undefined) || ''

  // P1.4: derive Lead/CC role for the viewing user.
  // targets is a comma-list; first entry = primary recipient (matches
  // server A.1 followup routing). Missing currentUserName → neutral.
  const targetsRaw = (run.attrs.targets as string | undefined) || ''
  const targetsList = targetsRaw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  const primaryRecipient = targetsList[0]
  const isUserPrimary = !!(
    currentUserName &&
    primaryRecipient &&
    primaryRecipient === currentUserName
  )
  const isUserCC = !!(
    currentUserName &&
    targetsList.includes(currentUserName) &&
    !isUserPrimary
  )
  // Has the viewing user already replied? Sticky-Banner auto-dismisses
  // after the first reaction (Alex Iter-3 decision).
  const userHasReplied = !!(
    currentUserName &&
    run.paragraphs.some(
      (p) =>
        p.section_role === 'reply' &&
        (p.author_name === currentUserName ||
          (typeof p.author_id === 'string' &&
            p.author_id.endsWith(`:${currentUserName}`))),
    )
  )
  const showPrimaryBanner =
    isUserPrimary && status === 'open' && !userHasReplied

  // Body split: main body vs reply-thread, filtering tag-only lines.
  const mainBody: StructuredParagraph[] = []
  const replyRuns: Array<{
    attrs: Record<string, unknown>
    paragraphs: StructuredParagraph[]
  }> = []
  let curReply: typeof replyRuns[number] | null = null
  for (const p of run.paragraphs) {
    const t = (p.text || '').trim()
    if (/^<reply\b/i.test(t)) {
      curReply = {
        attrs: (p.section_attrs as Record<string, unknown>) || {},
        paragraphs: [],
      }
      replyRuns.push(curReply)
      continue
    }
    if (/^<\/reply>/i.test(t)) {
      curReply = null
      continue
    }
    if (TAG_RE.test(t)) continue
    if (curReply) {
      curReply.paragraphs.push(p)
    } else if (p.section_role === 'reply') {
      const last = replyRuns[replyRuns.length - 1]
      if (last) last.paragraphs.push(p)
      else mainBody.push(p)
    } else {
      mainBody.push(p)
    }
  }

  const anchorQuote =
    run.section_type === 'annotation'
      ? (run.attrs.anchor_quote as string | undefined) || ''
      : ''
  const isQuestionLike =
    run.section_type === 'question' || run.section_type === 'annotation'

  return (
    <details
      className={`inline-section inline-section--${run.section_type}`}
      data-section-id={run.section_id}
      open={open}
      onToggle={(e) => {
        const next = (e.currentTarget as HTMLDetailsElement).open
        if (next !== open) toggle()
      }}
    >
      <summary className="inline-section-summary">
        <span className="inline-section-toggle" aria-hidden="true">
          {open ? (
            <ChevronDown size={14} strokeWidth={2.5} />
          ) : (
            <ChevronRight size={14} strokeWidth={2.5} />
          )}
        </span>
        <span
          className="inline-section-chip"
          style={{ color: visual.accent }}
        >
          <visual.Icon size={13} strokeWidth={2} />
          {visual.label}
        </span>
        <span className="inline-section-title">{title}</span>
        {/* Personal Lead/CC chip — only renders when viewer matches a
            target. Codex Bug-Finding 2026-06-05 (t-d6af447b3850):
            currentUserName is the human display name; targets are
            agent session names. Owner (Alex) sees no badge because his
            name doesn't match. Personal chip stays for matching viewers;
            general viewer-independent target line below covers everyone. */}
        {(isUserPrimary || isUserCC) && (
          <span
            title={
              isUserPrimary
                ? 'Du bist Primary auf dieser Section — Reaktion erwartet'
                : 'Du bist CC — kannst beobachten, musst nicht reagieren'
            }
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              background: isUserPrimary ? '#fef3c7' : '#f3f4f6',
              color: isUserPrimary ? '#92400e' : '#6b7280',
              border: isUserPrimary ? '1px solid #f59e0b' : 'none',
            }}
          >
            {isUserPrimary ? '★ Primary' : 'CC'}
          </span>
        )}
        {status && (
          <span
            className="inline-section-status"
            style={{
              background: statusColor(status).bg,
              color: statusColor(status).fg,
            }}
          >
            {status}
          </span>
        )}
      </summary>
      {/* Viewer-independent target line (Codex Task t-d6af447b3850):
          immer sichtbar wer adressiert ist, unabhängig vom Viewer.
          Personal banner unten greift nur wenn viewer == primary. */}
      {targetsList.length > 0 && open && (
        <div
          style={{
            padding: '6px 14px',
            background: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
            fontSize: 11,
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: '#6b7280', fontWeight: 500 }}>An:</span>
          {targetsList.map((name, idx) => (
            <span
              key={name}
              style={{
                padding: '1px 8px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: idx === 0 ? 600 : 500,
                background: idx === 0 ? '#fef3c7' : '#f3f4f6',
                color: idx === 0 ? '#92400e' : '#6b7280',
                border: idx === 0 ? '1px solid #f59e0b' : 'none',
              }}
            >
              {idx === 0 ? '★ ' : ''}
              {name}
            </span>
          ))}
        </div>
      )}
      {/* P1.4 Primary-Banner: sticky bis erste Reaktion. Auto-dismiss
          nach reply, re-aktivierbar wenn neue requires_response push
          kommt (das ist runtime, kommt aus IACP — nicht aus attrs). */}
      {showPrimaryBanner && open && (
        <div
          style={{
            padding: '8px 14px',
            background: '#fef3c7',
            borderBottom: '1px solid #fbbf24',
            fontSize: 12,
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>★</span>
          <span>
            <strong>Du bist Primary auf dieser Section.</strong> Eine
            Reaktion (Reply, Edit oder Decline) wird erwartet.
          </span>
        </div>
      )}
      <div className="inline-section-body">
        {anchorQuote && (
          <blockquote className="inline-section-quote">
            „{anchorQuote}"
          </blockquote>
        )}
        {run.section_type === 'vote' && postId && (
          <VoteCard
            postId={postId}
            sectionId={run.section_id}
            attrs={run.attrs}
          />
        )}
        {mainBody.length > 0 &&
          splitSvgSegments(mainBody).map((seg, i) =>
            seg.kind === 'svg' ? (
              <div
                key={`svg-${i}`}
                className="inline-section-svg"
                style={{ overflowX: 'auto' }}
                dangerouslySetInnerHTML={{
                  __html: responsiveSvg(sanitizeSvg(seg.svg)),
                }}
              />
            ) : (
              <div
                key={`md-${i}`}
                className="inline-section-text"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(
                    seg.paragraphs.map((p) => p.text).join('\n\n'),
                  ),
                }}
              />
            ),
          )}
        {replyRuns.length > 0 && (
          <div className="inline-section-replies">
            <div className="inline-section-replies-label">
              {replyRuns.length === 1
                ? '1 Antwort'
                : `${replyRuns.length} Antworten`}
            </div>
            {replyRuns.map((r, i) => {
              const by = (r.attrs.by as string | undefined) || 'Unknown'
              const at = (r.attrs.at as string | undefined) || ''
              // Color the reply-author's name with their identity-derived
              // hue so the eye picks who's who at a glance (same palette
              // colorFromId uses for the doc-author stripes).
              const byColor = colorFromId(`agent:${by}`, by)
              return (
                <details key={i} className="inline-section-reply" open>
                  <summary className="inline-section-reply-head">
                    <ChevronDown
                      size={10}
                      strokeWidth={2.5}
                      className="inline-section-reply-toggle"
                    />
                    <strong style={{ color: byColor }}>{by}</strong>
                    {at && (
                      <span className="inline-section-reply-time">
                        {formatRelativeShort(at)}
                      </span>
                    )}
                  </summary>
                  <div
                    className="inline-section-reply-body"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(
                        r.paragraphs.map((p) => p.text).join('\n\n'),
                      ),
                    }}
                  />
                </details>
              )
            })}
          </div>
        )}
        {isQuestionLike && postId && (
          <div className="inline-section-footer">
            {replyOpen ? (
              <InlineReplyForm
                value={replyText}
                onChange={setReplyText}
                saving={replySaving}
                onSubmit={async () => {
                  const trimmed = replyText.trim()
                  if (!trimmed || !postId) return
                  const token = getAccessToken()
                  if (!token) return
                  setReplySaving(true)
                  try {
                    const r = await fetch(
                      `${CONTENT_API}/api/v1/posts/${postId}/sections/${run.section_id}/reply`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ text: trimmed }),
                      },
                    )
                    if (!r.ok) {
                      const t = await r.text()
                      alert(`Reply nicht gespeichert: ${r.status} ${t}`)
                      return
                    }
                    setReplyText('')
                    setReplyOpen(false)
                  } catch (e) {
                    alert(
                      `Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`,
                    )
                  } finally {
                    setReplySaving(false)
                  }
                }}
                onCancel={() => {
                  setReplyText('')
                  setReplyOpen(false)
                }}
              />
            ) : (
              <button
                type="button"
                className="inline-section-reply-btn"
                onClick={() => {
                  setReplyOpen(true)
                  onReply?.(run.section_id)
                }}
              >
                Antworten
              </button>
            )}
          </div>
        )}
      </div>
    </details>
  )
}

function InlineReplyForm({
  value,
  onChange,
  saving,
  onSubmit,
  onCancel,
}: {
  value: string
  onChange: (v: string) => void
  saving: boolean
  onSubmit: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return (
    <div className="inline-section-reply-form">
      <textarea
        ref={ref}
        className="inline-section-reply-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Deine Antwort (Markdown ok, Cmd/Ctrl+Enter zum Senden)"
        rows={3}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            onSubmit()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
      />
      <div className="inline-section-reply-form-actions">
        <button
          type="button"
          className="inline-section-reply-btn"
          onClick={onCancel}
        >
          Abbrechen
        </button>
        <button
          type="button"
          className="inline-section-reply-btn inline-section-reply-btn--primary"
          onClick={onSubmit}
          disabled={saving || !value.trim()}
        >
          {saving ? 'Sendet …' : 'Antwort senden'}
        </button>
      </div>
    </div>
  )
}

export function SectionMeta({
  attrs,
  type,
}: {
  attrs: Record<string, unknown>
  type: SectionRun['section_type']
}) {
  const items: Array<{ label: string; value: string }> = []
  const push = (label: string, key: string) => {
    const v = attrs[key]
    if (typeof v === 'string' && v) items.push({ label, value: v })
  }
  if (type === 'question') {
    push('Initiator', 'initiator')
    push('Targets', 'targets')
  } else if (type === 'task') {
    push('Assignee', 'assignee')
    push('Due', 'due')
  } else if (type === 'product') {
    push('Mode', 'mode')
    push('Goal', 'goal')
  }
  if (items.length === 0) return null
  return (
    <div className="inline-section-meta">
      {items.map((it) => (
        <span key={it.label} className="inline-section-meta-item">
          <strong>{it.label}:</strong> {it.value}
        </span>
      ))}
    </div>
  )
}

/**
 * PlainRunWithAuthors — author-color left-stripe per commit_id-group.
 * Mirrors the old renderHighlighted() in CollabReadView but localised
 * to a plain run inside the inline-section flow. Paragraphs sharing
 * (author_id, commit_id) get one continuous stripe; fenced code-blocks
 * stay together so marked() sees the full source.
 */
function PlainRunWithAuthors({
  paragraphs,
  renderMarkdown,
}: {
  paragraphs: StructuredParagraph[]
  renderMarkdown: (md: string) => string
}) {
  type Atom =
    | { kind: 'single'; p: StructuredParagraph }
    | { kind: 'codeblock'; paragraphs: StructuredParagraph[] }
  const atoms: Atom[] = []
  let i = 0
  while (i < paragraphs.length) {
    const p = paragraphs[i]
    const fence = ((p.text || '').match(/```/g) || []).length
    if (fence % 2 === 1) {
      const acc: StructuredParagraph[] = [p]
      let total = fence
      let j = i + 1
      while (j < paragraphs.length && total % 2 === 1) {
        const n = paragraphs[j]
        acc.push(n)
        total += ((n.text || '').match(/```/g) || []).length
        j++
      }
      atoms.push({ kind: 'codeblock', paragraphs: acc })
      i = j
    } else {
      atoms.push({ kind: 'single', p })
      i++
    }
  }
  interface CG {
    author_id: string | null
    author_name: string
    commit_id: string | null
    atoms: Atom[]
  }
  const lead = (a: Atom): StructuredParagraph =>
    a.kind === 'codeblock' ? a.paragraphs[0] : a.p
  const groups: CG[] = []
  for (const atom of atoms) {
    const l = lead(atom)
    const aid = l.author_id ?? null
    const cid = l.commit_id ?? null
    const last = groups[groups.length - 1]
    if (
      last &&
      aid !== null &&
      cid !== null &&
      last.author_id === aid &&
      last.commit_id === cid
    ) {
      last.atoms.push(atom)
    } else {
      groups.push({
        author_id: aid,
        author_name: l.author_name || 'Unknown',
        commit_id: cid,
        atoms: [atom],
      })
    }
  }
  return (
    <>
      {groups.map((g, gi) => {
        const hasAuthor = !!g.author_id
        const color = g.author_id
          ? colorFromId(g.author_id, g.author_name)
          : 'transparent'
        const style: React.CSSProperties = {
          borderLeft: hasAuthor
            ? `3px solid ${color}`
            : '3px solid transparent',
          paddingLeft: '8px',
          marginBottom: '0.8em',
        }
        return (
          <div
            key={`cg-${gi}`}
            style={style}
            data-author={hasAuthor ? g.author_name : ''}
          >
            {g.atoms.map((atom, ai) =>
              atom.kind === 'codeblock' ? (
                <div
                  key={`cb-${gi}-${ai}`}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(
                      atom.paragraphs.map((p) => p.text).join('\n\n'),
                    ),
                  }}
                />
              ) : (
                <div
                  key={`p-${gi}-${ai}`}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(atom.p.text || ''),
                  }}
                />
              ),
            )}
          </div>
        )
      })}
    </>
  )
}

function VoteCard({
  postId,
  sectionId,
  attrs,
}: {
  postId: number
  sectionId: string
  attrs: Record<string, unknown>
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const question = (attrs.question as string | undefined) || ''
  const optionsRaw = (attrs.options as string | undefined) || ''
  const optionsOpen = optionsRaw.trim().toLowerCase() === 'open'
  const options = optionsOpen
    ? []
    : optionsRaw.split(',').map((s) => s.trim()).filter(Boolean)
  const status = (attrs.status as string | undefined) || 'open'
  const winner = (attrs.winner as string | undefined) || ''
  const threshold = (attrs.threshold as string | undefined) || 'majority'
  const votersRaw = (attrs.voters as string | undefined) || 'open'
  const votesRaw = (attrs.votes as string | undefined) || ''
  const actionType = (attrs.action_type as string | undefined) || ''
  const actionTarget = (attrs.action_target as string | undefined) || ''
  const actionOutcome = (attrs.action_outcome as string | undefined) || ''
  const tiedAt = (attrs.tied_at as string | undefined) || ''
  const tiedBy = (attrs.tied_by as string | undefined) || ''
  const talliedAt = (attrs.tallied_at as string | undefined) || ''

  // Parse votes into { choice → [voter] }. Choice values are
  // percent-encoded server-side so commas in free-text answers
  // (options='open' mode) don't shred the storage. decodeURIComponent
  // round-trips. Older (pre-encoding) entries pass through unchanged.
  const votesByChoice = new Map<string, string[]>()
  const cast = new Map<string, string>()
  for (const entry of votesRaw.split(',')) {
    const e = entry.trim()
    if (!e || !e.includes(':')) continue
    const idx = e.indexOf(':')
    const voter = e.slice(0, idx).trim()
    let choice = e.slice(idx + 1).trim()
    if (!voter || !choice) continue
    try {
      choice = decodeURIComponent(choice)
    } catch {
      /* legacy unencoded value — keep as-is */
    }
    cast.set(voter, choice)
    if (!votesByChoice.has(choice)) votesByChoice.set(choice, [])
    votesByChoice.get(choice)!.push(voter)
  }
  const totalCast = cast.size

  const castVote = async (choice: string) => {
    setErr(null)
    setBusy(true)
    try {
      const token = getAccessToken()
      if (!token) {
        setErr('Nicht eingeloggt')
        return
      }
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/sections/${sectionId}/vote`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ choice }),
        },
      )
      if (!r.ok) {
        const body = await r.text()
        let msg = body
        try {
          const j = JSON.parse(body) as { detail?: { error?: string; hint?: string } }
          msg = j.detail?.hint || j.detail?.error || body
        } catch {
          /* ignore */
        }
        setErr(`${r.status}: ${msg}`)
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const isOpen = status === 'open'
  const isTallied = status === 'tallied'
  const isTied = status === 'tied'

  return (
    <div className="inline-vote">
      {question && <div className="inline-vote-question">{question}</div>}
      <div className="inline-vote-meta">
        <span>
          Schwelle: <strong>{threshold}</strong>
        </span>
        <span>
          Wähler:{' '}
          <strong>{votersRaw === 'open' ? 'offen' : votersRaw}</strong>
        </span>
        <span>
          Stimmen abgegeben: <strong>{totalCast}</strong>
        </span>
      </div>

      {isTallied && winner && (
        <div className="inline-vote-result inline-vote-result--win">
          <Trophy size={14} strokeWidth={2.5} />
          <span>
            Gewinner: <strong>{winner}</strong>
          </span>
          {talliedAt && (
            <span className="inline-vote-result-time">
              {formatRelativeShort(talliedAt)}
            </span>
          )}
        </div>
      )}
      {isTied && (
        <div className="inline-vote-result inline-vote-result--tied">
          <Scale size={14} strokeWidth={2.5} />
          <span>
            Unentschieden — kein klarer Gewinner
            {tiedBy ? ` (geschlossen durch ${tiedBy})` : ''}
          </span>
          {tiedAt && (
            <span className="inline-vote-result-time">
              {formatRelativeShort(tiedAt)}
            </span>
          )}
        </div>
      )}

      {!optionsOpen && options.length > 0 && (
        <div className="inline-vote-options">
          {options.map((opt) => {
            const voters = votesByChoice.get(opt) || []
            const isWin = isTallied && winner === opt
            return (
              <button
                key={opt}
                type="button"
                className={
                  'inline-vote-option' +
                  (isWin ? ' inline-vote-option--win' : '')
                }
                disabled={!isOpen || busy}
                onClick={() => castVote(opt)}
                title={
                  voters.length
                    ? `Wähler: ${voters.join(', ')}`
                    : 'Noch keine Stimme'
                }
              >
                <span className="inline-vote-option-label">{opt}</span>
                <span className="inline-vote-option-count">
                  {voters.length}
                </span>
              </button>
            )
          })}
        </div>
      )}
      {cast.size > 0 && (
        <div className="inline-vote-castlist">
          <div className="inline-vote-castlist-label">
            Abgegebene Stimmen
          </div>
          {Array.from(cast.entries()).map(([voter, choice]) => {
            const voterColor = colorFromId(`agent:${voter}`, voter)
            return (
              <div className="inline-vote-castlist-row" key={voter}>
                <strong style={{ color: voterColor }}>{voter}</strong>
                <span className="inline-vote-castlist-arrow">→</span>
                <span className="inline-vote-castlist-choice">{choice}</span>
              </div>
            )
          })}
        </div>
      )}
      {optionsOpen && isOpen && (
        <FreeFormVoteInput busy={busy} onSubmit={castVote} />
      )}

      {actionType && actionType !== 'none' && (
        <div className="inline-vote-action">
          <span>
            Ableitung:{' '}
            <code>{actionType}</code>
            {actionTarget && (
              <>
                {' → '}
                <code>{actionTarget}</code>
              </>
            )}
          </span>
          {actionOutcome && (
            <span
              className={
                'inline-vote-action-outcome inline-vote-action-outcome--' +
                actionOutcome
              }
            >
              {actionOutcome}
            </span>
          )}
        </div>
      )}

      {err && <div className="inline-vote-error">{err}</div>}
    </div>
  )
}

function FreeFormVoteInput({
  busy,
  onSubmit,
}: {
  busy: boolean
  onSubmit: (choice: string) => void
}) {
  const [val, setVal] = useState('')
  return (
    <div className="inline-vote-freeform">
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Deine Antwort"
        disabled={busy}
      />
      <button
        type="button"
        disabled={busy || !val.trim()}
        onClick={() => {
          const v = val.trim()
          if (!v) return
          onSubmit(v)
          setVal('')
        }}
      >
        Stimmen
      </button>
    </div>
  )
}

function visualForType(t: string) {
  switch (t) {
    case 'product':
      return { label: 'Product', Icon: Box, accent: '#3b82f6' }
    case 'question':
      return { label: 'Frage', Icon: HelpCircle, accent: '#f59e0b' }
    case 'task':
      return { label: 'Aufgabe', Icon: CheckCircle, accent: '#10b981' }
    case 'annotation':
      return { label: 'Anmerkung', Icon: StickyNote, accent: '#a855f7' }
    case 'vote':
      return { label: 'Abstimmung', Icon: VoteIcon, accent: '#C41E3A' }
    case 'vision':
      return { label: 'Vision', Icon: Lightbulb, accent: '#eab308' }
    case 'suggestion':
      return {
        label: 'Vorschlag', Icon: GitPullRequest, accent: '#06b6d4',
      }
    default:
      return { label: t || 'Section', Icon: Box, accent: '#6b7280' }
  }
}

function statusColor(status: string): { bg: string; fg: string } {
  switch (status) {
    case 'open':
      return { bg: '#fef3c7', fg: '#78350f' }
    case 'resolved':
    case 'closed':
    case 'done':
      return { bg: '#dcfce7', fg: '#14532d' }
    case 'in_progress':
      return { bg: '#dbeafe', fg: '#1e3a8a' }
    default:
      return { bg: '#e5e7eb', fg: '#374151' }
  }
}

function formatRelativeShort(iso: string): string {
  try {
    const t = new Date(iso).getTime()
    const diff = Math.max(0, Date.now() - t)
    const m = Math.floor(diff / 60_000)
    if (m < 60) return `vor ${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `vor ${h}h`
    const d = Math.floor(h / 24)
    return `vor ${d}d`
  } catch {
    return ''
  }
}
